// types
import {
	TraceabilityTransactions,
	TraceabilityTransaction,
	TraceabilityType,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Chip, ChipProps, Tooltip } from '@mui/material';

// ui
import { Loading } from '../Loading/Loading';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatNumber, formatPercentage } from '../../../utils/format';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface ChipTraceabilityProps {
	isLoading?: boolean;
	traceability?: TraceabilityTransactions | TraceabilityTransaction;
	type?: TraceabilityType;
}

export const ChipTraceability = ({
	isLoading,
	traceability,
	type = 'TRANSACTIONS',
}: ChipTraceabilityProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const isObject = typeof traceability === 'object';
	const isTransactions = type === 'TRANSACTIONS' && isObject && 'documentation' in traceability;
	const isFinancial = type === 'FINANCIAL' && isObject && 'financial' in traceability;

	let color: ChipProps['color'] = 'default';
	let documents: TraceabilityTransaction['documents'] | undefined;
	let value = 0;

	// or financial traceability display if feature flag is disabled
	if (type === 'FINANCIAL' && !featureFlags['FINANCIAL_TRACEABILITY']) {
		return null;
	}

	if (isLoading) {
		return <Loading size="xs" />;
	}

	if (traceability) {
		documents = (traceability as TraceabilityTransaction).documents;

		let traceabilityValue = null;

		if ('value' in traceability) {
			traceabilityValue = traceability.value;
		} else if (isTransactions) {
			traceabilityValue = traceability.documentation.value;
		} else if (isFinancial) {
			traceabilityValue = traceability.financial.value;
		}

		if (typeof traceabilityValue === 'number') {
			value = traceabilityValue;

			if (traceabilityValue <= 49) {
				color = 'error';
			} else if (traceabilityValue >= 50 && traceabilityValue <= 70) {
				color = 'warning';
			} else if (traceabilityValue >= 71) {
				color = 'success';
			}
		}
	}

	const chip = (
		<Chip color={color} label={formatPercentage(value)} size="small" variant="twoTone" />
	);

	if (isTransactions) {
		return (
			<Tooltip
				arrow={true}
				placement="top"
				title={
					<Box component="ul" sx={{ m: 0, p: 0, listStyleType: 'none' }}>
						<li>
							{formatNumber(traceability?.documentation?.documented || 0, {
								notation: 'compact',
							})}{' '}
							{t('fullyEvidenced')}
						</li>
						<li>
							{formatNumber(traceability?.documentation?.partial || 0, {
								notation: 'compact',
							})}{' '}
							{t('partiallyEvidenced')}
						</li>
						<li>
							{formatNumber(traceability?.documentation?.undocumented || 0, {
								notation: 'compact',
							})}{' '}
							{t('notEvidenced')}
						</li>
					</Box>
				}>
				{chip}
			</Tooltip>
		);
	} else if (isFinancial && featureFlags['FINANCIAL_TRACEABILITY']) {
		return (
			<Tooltip
				arrow={true}
				placement="top"
				title={
					<Box component="ul" sx={{ m: 0, p: 0, listStyleType: 'none' }}>
						{traceability?.financial?.currencies?.map((c) => (
							<li key={c.currency}>
								{t('xOfY', {
									x: formatCurrency({
										currency: c.currency,
										notation: 'compact',
										value: c.documented || 0,
									}),
									y: formatCurrency({
										currency: c.currency,
										notation: 'compact',
										value: c.total || 0,
									}),
								})}
							</li>
						))}
					</Box>
				}>
				{chip}
			</Tooltip>
		);
	} else if (documents) {
		return (
			<Tooltip
				arrow={true}
				placement="top"
				title={t('traceabilityDocuments', {
					required: documents.required.length,
					uploaded: documents.uploaded.length,
				})}>
				{chip}
			</Tooltip>
		);
	}

	return chip;
};
